/**
 * <flavor-selector> — companion to blocks/flavor-selector.liquid.
 *
 * Keeps the flavor swatch links in step with the buy box's 30 / 90 tile so a
 * shopper who picked 90 Servings lands on the sibling flavor's PDP with 90
 * still selected. Writes or removes `?buybox=<tile>` on each swatch href; the
 * receiving page's <subscription-options> reads it back in #seedInitialState
 * (assets/subscription-options.js). The links work without this script —
 * the shopper then lands on the sibling's default tile.
 *
 * Re-synced on every tile `change` (delegated at document level, since the
 * tiles live in a sibling block) and again on swatch click, which also covers
 * tiles ticked programmatically (URL seeding sets `.checked` without firing
 * `change`).
 */
class FlavorSelector extends HTMLElement {
  /** @type {(event: Event) => void} */
  #onChange = (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (target && target.matches('[data-buybox-product-tile]')) this.#sync();
  };

  /** @type {(event: Event) => void} */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target && target.closest('a[data-flavor-link]')) this.#sync();
  };

  connectedCallback() {
    this.#sync();
    document.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
  }

  #sync() {
    const scope = this.closest('.product-details') ?? document;
    const tile = /** @type {HTMLInputElement | null} */ (
      scope.querySelector('[data-buybox-product-tile]:checked')
    );
    const key = tile?.dataset.buyboxProductTile ?? null;

    this.querySelectorAll('a[data-flavor-link]').forEach((anchor) => {
      const base = anchor.dataset.href || anchor.getAttribute('href');
      if (!base) return;
      anchor.dataset.href = base;
      const url = new URL(base, window.location.origin);
      if (key && key !== 'main') {
        url.searchParams.set('buybox', key);
      } else {
        url.searchParams.delete('buybox');
      }
      anchor.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    });
  }
}

if (!customElements.get('flavor-selector')) {
  customElements.define('flavor-selector', FlavorSelector);
}
